/* eslint-disable react-hooks/exhaustive-deps */
import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons/faMapMarkerAlt';
import { FieldLabelIconMui5 } from 'components/atoms/FieldLabelIcon';
import ZipTextInput from 'components/molecules/ZipTextInput';
import isEmpty from 'lodash/isEmpty';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import { SetFilterValue, useFilters } from 'services/filters/hooks/useFilters';
import { useZipcodeValidator } from 'services/zipcode/hooks/useZipcodeValidator';
import { filterZipcodeAtom } from 'services/zipcode/recoil';
import { ZIP_CODE_MAX_LENGTH } from '../../../../../shared/utils/constants';

export type ValidatorErrorType = Awaited<
  ReturnType<ReturnType<typeof useZipcodeValidator>['isZipcodeValid']>
>;

type ValidationsStateType = {
  [key: string]: ValidatorErrorType;
};

const ZIPCODE_REQUIRED_ERROR_MESSAGE = 'Enter 5 digits';

export const ZipcodeFilter = ({
  setFilterValue,
  disabled = false,
}: {
  setFilterValue: SetFilterValue;
  disabled?: boolean;
}) => {
  const { filters } = useFilters();
  const { isZipcodeValid } = useZipcodeValidator();
  const { zipcode } = filters;
  const [zipcodeAtom, setZipcodeAtom] =
    useRecoilState<string>(filterZipcodeAtom);

  const [zipcodeState, setZipcodeState] = useState<string>('');
  const [validationsState, setValidationsState] =
    useState<ValidationsStateType>({});

  const isUpdatePending = useRef<boolean>(false);

  const isGoodLength = (zipcode: string | undefined) => {
    return zipcode?.length > 4;
  };

  const updateZipcodeValidation = async (zipcode: string) => {
    if (!isGoodLength(zipcode)) {
      return;
    }

    const validationResult = await isZipcodeValid(zipcode || '');
    setValidationsState((prev) => ({
      ...prev,
      [zipcode]: validationResult,
    }));
  };

  useEffect(() => {
    if (zipcode !== zipcodeAtom) {
      return;
    }
    setZipcodeState(filters.zipcode);
  }, [filters]);

  useEffect(() => {
    const initialZipcode = zipcodeAtom || zipcode || '';
    setZipcodeState(() => initialZipcode);
    updateZipcodeValidation(initialZipcode);
  }, [zipcodeAtom]);

  useEffect(() => {
    if (
      !isUpdatePending.current ||
      !validationsState[zipcodeState]?.isValid ||
      !isGoodLength(zipcodeState)
    ) {
      return;
    }
    setZipcodeAtom(() => zipcodeState);
    setFilterValue('zipcode', zipcodeState);
    isUpdatePending.current = false;
  }, [validationsState[zipcodeState]]);

  const onZipCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }
    const zipcode = String(event.target.value || '');
    setZipcodeState(() => zipcode);
    updateZipcodeValidation(zipcode);
    isUpdatePending.current = true;
  };

  const isZipcodeSoftValid =
    isGoodLength(zipcodeState) || isEmpty(zipcodeState);

  const helperText = isZipcodeSoftValid
    ? validationsState[zipcodeState]?.error
    : ZIPCODE_REQUIRED_ERROR_MESSAGE;

  const isValid = isZipcodeSoftValid
    ? validationsState[zipcodeState]?.isValid ?? true
    : false;

  return (
    <ZipTextInput
      value={zipcodeState}
      onChange={onZipCodeChange}
      helperText={helperText}
      error={!isValid}
      fullWidth
      size='small'
      variant='outlined'
      placeholder='Enter ZIP code'
      inputProps={{ maxLength: ZIP_CODE_MAX_LENGTH }}
      label={
        <>
          {' '}
          <FieldLabelIconMui5 icon={faMapMarkerAlt} /> Location{' '}
        </>
      }
    />
  );
};
